import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { timeAgo } from '../../utils/dateUtils';
import { ICONS } from '../../constants/imageConstants';
import { usePresence } from '../../hooks/usePresence';

const ConversationListItem = ({ conversation }) => {
  const navigate = useNavigate();
  const { id, name, avatar, lastMessage, timestamp, unreadCount, isLoadingMessage } = conversation;

  const presence = usePresence(id);
  const isOnline = presence.state === 'online';
  const hasUnread = unreadCount > 0 && !isLoadingMessage;

  const handleClick = () => {
    navigate(`/chat/${id}`, { state: { name, avatar } });
  };

  return (
    <motion.div
      layout
      transition={{
        type: 'spring',
        stiffness: 350,
        damping: 35,
        mass: 0.8
      }}
      onClick={handleClick}
      className="flex items-center p-3 space-x-4 rounded-xl hover:bg-gray-50 cursor-pointer transition-colors duration-200"
    >
      {/* Avatar with online dot */}
      <div className="relative flex-shrink-0">
        <img
          src={avatar || ICONS.USER_AVATAR}
          alt={name}
          className="w-12 h-12 rounded-full object-cover"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = ICONS.USER_AVATAR;
          }}
        />
        {isOnline && (
          <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-center mb-0.5">
          <h3 className={`text-sm truncate ${hasUnread ? 'font-bold text-gray-900' : 'font-semibold text-gray-800'}`}>
            {name}
          </h3>
          {timestamp && timeAgo(timestamp) !== 'Unknown' && (
            <span className={`text-xs flex-shrink-0 ml-2 ${hasUnread ? 'text-[var(--btn-primary-bg)] font-semibold' : 'text-gray-400'}`}>
              {timeAgo(timestamp)}
            </span>
          )}
        </div>
        <div className="flex justify-between items-center">
          {isLoadingMessage ? (
            <div className="h-4 bg-gray-200 rounded animate-pulse w-32 mt-1" />
          ) : (
            <p className={`text-sm truncate pr-2 ${
              lastMessage === 'No messages yet'
                ? 'italic text-gray-400'
                : hasUnread ? 'text-gray-900 font-medium' : 'text-gray-500'
            }`}>
              {lastMessage}
            </p>
          )}
          {hasUnread && (
            <span className="flex-shrink-0 min-w-[20px] h-5 px-1.5 bg-brand text-white text-[10px] font-bold flex items-center justify-center rounded-full">
              {unreadCount > 20 ? '20+' : unreadCount}
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ConversationListItem;